import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import { type DebriefFact, DebriefFactSource } from '@momentum/types';
import { cn } from '@/shared/utils/utils';
import { IntegrationIcon } from '../../gather-facts-stage/components/IntegrationIcon';
import { toViewableFactUrl } from '../../shared/utils/factLink';

interface MatchPointFactRowProps {
  fact: DebriefFact;
}

export function MatchPointFactRow({ fact }: MatchPointFactRowProps) {
  const { t } = useTranslation('debriefV2');
  const aiSource =
    fact.source !== DebriefFactSource.Manual ? fact.source : null;

  return (
    <div className="group flex items-start gap-3 py-1.5 relative rounded-lg -ms-2 ps-2 pe-2">
      <div className="w-1.5 h-1.5 rounded-full bg-slate-300 mt-2 shrink-0" />
      <div className="flex-1 min-w-0 pe-10">
        <p className="text-sm text-slate-800 leading-relaxed pt-0.5 inline">
          {fact.description}
        </p>
        {aiSource && <SourceChip source={aiSource} title={fact.title} />}
      </div>
      {aiSource && (
        <a
          href={toViewableFactUrl(fact.link)}
          target="_blank"
          rel="noopener noreferrer"
          title={t('gather.factRow.viewSource')}
          aria-label={t('gather.factRow.viewSource')}
          className="absolute end-2 top-1.5 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-md text-slate-400 hover:text-primary"
        >
          <ExternalLink className="h-4 w-4" aria-hidden />
        </a>
      )}
    </div>
  );
}

type AiFactSource = Exclude<DebriefFactSource, DebriefFactSource.Manual>;

interface SourceChipProps {
  source: AiFactSource;
  title: string;
}

function SourceChip({ source, title }: SourceChipProps) {
  const isJira = source === DebriefFactSource.Jira;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 ms-2 px-2 py-0.5 rounded-md border text-[11px] font-semibold align-text-bottom',
        isJira
          ? 'bg-blue-50 border-blue-100 text-blue-700'
          : 'bg-white border-slate-200 text-slate-600 shadow-sm'
      )}
    >
      <IntegrationIcon source={source} className="h-3 w-3" />
      {title}
    </span>
  );
}
